import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Pencil, Trash2, X, FileCheck,
  Building2, Stethoscope, FlaskConical, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore, ROL } from '@/store/auth.store';
import { getErrorMessage } from '@/lib/errors';

// ─── Types ───────────────────────────────────────────────────────────────────

type TipoConsentimiento = 'clinica' | 'medico' | 'tratamiento';

interface Plantilla {
  id: string;
  nombre: string;
  tipo: TipoConsentimiento;
  contenido: string;
  activo: boolean;
  medicoId?: string;
  procedimientoId?: string;
}

const TIPO_INFO: Record<TipoConsentimiento, { label: string; icon: typeof Building2; cls: string; desc: string }> = {
  clinica:     { label: 'Clínica',     icon: Building2,    cls: 'bg-blue-50 text-blue-700 border-blue-200',     desc: 'Aplica a todos los pacientes de la clínica' },
  medico:      { label: 'Médico',      icon: Stethoscope,  cls: 'bg-violet-50 text-violet-700 border-violet-200', desc: 'Asociado a un médico específico' },
  tratamiento: { label: 'Tratamiento', icon: FlaskConical,  cls: 'bg-teal-50 text-teal-700 border-teal-200',    desc: 'Para un procedimiento o tratamiento' },
};

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

// ─── Form Modal ───────────────────────────────────────────────────────────────

interface FormModal {
  plantilla: Plantilla | null;
  onClose: () => void;
  onSaved: () => void;
  clinicaId: string;
  usuarioId: string;
}

function PlantillaFormModal({ plantilla, onClose, onSaved, clinicaId, usuarioId }: FormModal) {
  const [nombre,    setNombre]    = useState(plantilla?.nombre    || '');
  const [tipo,      setTipo]      = useState<TipoConsentimiento>(plantilla?.tipo || 'clinica');
  const [contenido, setContenido] = useState(plantilla?.contenido || '');

  const saveMutation = useMutation({
    mutationFn: (dto: any) => plantilla
      ? api.patch(`/api/plantillas-consentimiento/${plantilla.id}`, dto).then(r => r.data)
      : api.post('/api/plantillas-consentimiento', dto).then(r => r.data),
    onSuccess: () => {
      toast.success(plantilla ? 'Plantilla actualizada' : 'Plantilla creada');
      onSaved();
      onClose();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar la plantilla')),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nombre.trim()) { toast.error('El nombre es requerido'); return; }
    if (!contenido.trim()) { toast.error('El contenido es requerido'); return; }
    saveMutation.mutate({ nombre, tipo, contenido, clinicaId, medicoUsuarioId: tipo === 'medico' ? usuarioId : undefined });
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-text-primary text-sm">
            {plantilla ? 'Editar plantilla' : 'Nueva plantilla de consentimiento'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Nombre de la plantilla *</label>
            <input className={inputCls} value={nombre} onChange={e => setNombre(e.target.value)}
              placeholder="Ej. Consentimiento general de tratamiento..." />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-2">Alcance *</label>
            <div className="grid grid-cols-3 gap-2">
              {(Object.entries(TIPO_INFO) as [TipoConsentimiento, typeof TIPO_INFO['clinica']][]).map(([key, info]) => {
                const Icon = info.icon;
                const isActive = tipo === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTipo(key)}
                    className={cn(
                      'flex flex-col items-start gap-1.5 p-3 rounded-xl border text-left transition-all',
                      isActive ? `${info.cls} border-current` : 'border-gray-200 hover:border-gray-300'
                    )}>
                    <div className="flex items-center justify-between w-full">
                      <Icon size={15} className={isActive ? '' : 'text-text-secondary'} />
                      {isActive && <Check size={12} />}
                    </div>
                    <p className={cn('text-xs font-semibold', isActive ? '' : 'text-text-primary')}>{info.label}</p>
                    <p className={cn('text-[10px]', isActive ? 'opacity-80' : 'text-text-secondary')}>{info.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Contenido del consentimiento *</label>
            <textarea
              rows={14}
              className={`${inputCls} resize-y font-mono text-xs leading-relaxed`}
              value={contenido}
              onChange={e => setContenido(e.target.value)}
              placeholder="Escribe el texto completo del consentimiento informado..."
            />
            <p className="text-[10px] text-text-secondary mt-1">Texto plano. El paciente lo leerá antes de firmar.</p>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 bg-white">
          <button onClick={onClose}
            className="text-sm border border-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50 text-text-secondary transition-colors">
            Cancelar
          </button>
          <button
            onClick={e => { e.preventDefault(); handleSubmit(e as any); }}
            disabled={saveMutation.isPending}
            className="flex items-center gap-1.5 text-sm bg-primary-600 text-white px-5 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors font-medium">
            <Check size={14} />
            {saveMutation.isPending ? 'Guardando...' : 'Guardar plantilla'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PlantillasConsentimientoPage() {
  const { usuario } = useAuthStore();
  const qc = useQueryClient();
  const canWrite = usuario?.rolId === ROL.SUPER_ADMIN || usuario?.rolId === ROL.ADMIN_CLINICA || usuario?.rolId === ROL.MEDICO || usuario?.rolId === ROL.MEDICO_ADMIN;

  const [filterTipo, setFilterTipo] = useState<TipoConsentimiento | ''>('');
  const [editando,   setEditando]   = useState<Plantilla | null | 'new'>(null);

  const { data: plantillas = [], isLoading } = useQuery<Plantilla[]>({
    queryKey: ['plantillas-consentimiento'],
    queryFn: () => api.get('/api/plantillas-consentimiento?activo=all').then(r => r.data),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      api.patch(`/api/plantillas-consentimiento/${id}`, { activo }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plantillas-consentimiento'] });
      toast.success('Plantilla actualizada');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar')),
  });

  const filtered = plantillas.filter(p => !filterTipo || p.tipo === filterTipo);

  const totals = {
    clinica:     plantillas.filter(p => p.tipo === 'clinica'     && p.activo).length,
    medico:      plantillas.filter(p => p.tipo === 'medico'      && p.activo).length,
    tratamiento: plantillas.filter(p => p.tipo === 'tratamiento' && p.activo).length,
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <FileCheck size={20} className="text-primary-600" />
            Plantillas de consentimiento
          </h1>
          <p className="text-sm text-text-secondary mt-0.5">
            Gestiona los textos de consentimiento informado por alcance
          </p>
        </div>
        {canWrite && (
          <button
            onClick={() => setEditando('new')}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors">
            <Plus size={14} /> Nueva plantilla
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {(Object.entries(TIPO_INFO) as [TipoConsentimiento, typeof TIPO_INFO['clinica']][]).map(([key, info]) => {
          const Icon = info.icon;
          return (
            <div key={key} className={cn('rounded-xl border p-4 flex items-center gap-3', info.cls)}>
              <Icon size={20} />
              <div>
                <p className="text-xl font-bold">{totals[key]}</p>
                <p className="text-xs opacity-80">{info.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1.5">
        <button
          onClick={() => setFilterTipo('')}
          className={cn('text-xs px-3 py-1.5 rounded-full border transition-colors',
            filterTipo === '' ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-200 text-text-secondary hover:border-gray-400'
          )}>
          Todas ({plantillas.length})
        </button>
        {(Object.entries(TIPO_INFO) as [TipoConsentimiento, typeof TIPO_INFO['clinica']][]).map(([key, info]) => (
          <button
            key={key}
            onClick={() => setFilterTipo(key)}
            className={cn('text-xs px-3 py-1.5 rounded-full border transition-colors',
              filterTipo === key ? `${info.cls} font-semibold` : 'border-gray-200 text-text-secondary hover:border-gray-400'
            )}>
            {info.label} ({plantillas.filter(p => p.tipo === key).length})
          </button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <p className="text-center py-10 text-sm text-text-secondary">Cargando...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-200 rounded-xl">
          <FileCheck size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-text-secondary">Sin plantillas{filterTipo ? ` de tipo ${TIPO_INFO[filterTipo].label}` : ''}</p>
          {canWrite && (
            <button onClick={() => setEditando('new')} className="mt-3 text-xs text-primary-600 hover:underline">
              Crear primera plantilla
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => {
            const info = TIPO_INFO[p.tipo] || TIPO_INFO.clinica;
            const Icon = info.icon;
            return (
              <div key={p.id} className={cn(
                'rounded-xl border bg-white p-4 flex items-start gap-4 transition-opacity',
                !p.activo ? 'opacity-50' : ''
              )}>
                <span className={cn('flex items-center justify-center w-9 h-9 rounded-lg border flex-shrink-0', info.cls)}>
                  <Icon size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold text-text-primary">{p.nombre}</p>
                    {!p.activo && (
                      <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">Inactiva</span>
                    )}
                  </div>
                  <p className={cn('text-[10px] font-semibold', info.cls.split(' ')[1])}>{info.label}</p>
                  <p className="text-xs text-text-secondary mt-1 line-clamp-2">{p.contenido}</p>
                </div>
                {canWrite && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setEditando(p)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary hover:text-text-primary transition-colors">
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => toggleMutation.mutate({ id: p.id, activo: !p.activo })}
                      title={p.activo ? 'Desactivar' : 'Activar'}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-text-secondary hover:text-red-500 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Form modal */}
      {editando !== null && (
        <PlantillaFormModal
          plantilla={editando === 'new' ? null : editando}
          onClose={() => setEditando(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['plantillas-consentimiento'] })}
          clinicaId={usuario?.clinicaId || ''}
          usuarioId={usuario?.id || ''}
        />
      )}
    </div>
  );
}
